import React, { useEffect, useState, useCallback } from "react";
import { CDBIcon } from "cdbreact";
import { Pagination } from "react-bootstrap";
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import NavBarPeiix from '../../NavBar';
import '../../../Styles/SpinnerOverlay.css';
import { Spin } from "antd";
import LoadingOverlay from '../../common/LoadingOverlay';

// Nuevos imports modulares
import { fetchDepositos, fetchMonthlySum, computeAccountType } from './Movements/services/movementService';
import MovementsTable from './Movements/components/MovementsTable';
import MovementsSummary from './Movements/components/MovementsSummary';

const Movimientos = () => {
    // Estados principales
    const [depositos, setDepositos] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [selectedSucursal, setSelectedSucursal] = useState(null);
    const [nombreComercial, setNombreComercial] = useState('');
    
    // Estados de UI/Carga
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    
    // Estados de Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // Estados de Datos
    const [monthlyTotal, setMonthlyTotal] = useState(0);
    const [isAccount, setIsAccount] = useState(null);
    const [isAggregator, setIsAggregator] = useState(null);

    const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

    // 1. Carga inicial de datos de usuario/sucursales
    useEffect(() => {
        const nombre = localStorage.getItem('nombre_comercial');
        if (nombre) setNombreComercial(nombre);
        
        const accountStatus = localStorage.getItem('is_account') === 'true';
        const aggregatorStatus = localStorage.getItem('is_aggregator') === 'true';
        setIsAccount(accountStatus);
        setIsAggregator(aggregatorStatus);

        if (accountStatus) {
            const existingUserTokens = localStorage.getItem('user_tokens_related');
            let userTokensRelated = [];
            try {
                userTokensRelated = existingUserTokens ? JSON.parse(existingUserTokens) : [];
            } catch (err) {
                userTokensRelated = [];
            }

            const cuentaPrincipal = {
                nombre: localStorage.getItem('nombre_comercial'),
                access_token: localStorage.getItem('access_token'),
                bp_token: localStorage.getItem('bp_token'),
                user_token: localStorage.getItem('user_token'),
                refresh_token: localStorage.getItem('refresh_token'),
            };

            const sucursalData = userTokensRelated.map(item => ({
                nombre: item.nombre_comercial,
                bp_token: item.bp_token,
                access_token: item.access_token,
                user_token: item.user_token,
                refresh_token: item.refresh_token,
            }));

            sucursalData.unshift(cuentaPrincipal);
            setSucursales(sucursalData);
            setSelectedSucursal(cuentaPrincipal);
        } else {
            const cuentitaBranches = localStorage.getItem('cuentita_branches');
            let branchNames = [];
            try {
                branchNames = cuentitaBranches ? JSON.parse(cuentitaBranches) : [];
            } catch (err) {
                branchNames = [];
            }
            
            const cuentitaSucursales = branchNames.map(name => ({ nombre: name }));
            const allBranchesOption = { nombre: "Todas las sucursales", isAll: true };
            cuentitaSucursales.unshift(allBranchesOption);
            setSucursales(cuentitaSucursales);
            setSelectedSucursal(allBranchesOption);
        }
    }, []);

    // 2. Función para obtener depósitos (usando el servicio)
    const loadDepositos = useCallback(async () => {
        if (isAccount === null || isAggregator === null) return;
        
        setLoading(true);
        setError(null);

        const accountType = computeAccountType();
        let accessToken, bpToken = null;

        if (isAccount && !isAggregator) {
            accessToken = selectedSucursal ? selectedSucursal.access_token : localStorage.getItem('access_token');
            bpToken = selectedSucursal ? selectedSucursal.bp_token : localStorage.getItem('bp_token');
        } else {
            accessToken = localStorage.getItem('access_token');
        }

        try {
            const result = await fetchDepositos({
                accessToken,
                bpToken,
                accountType,
                page: currentPage
            });

            if (result.error === 'UNAUTHORIZED') {
                // Aquí se podría implementar la lógica de refresh si no se maneja en el fetchHelper
                setError('Sesión expirada. Por favor vuelva a iniciar sesión.');
            } else {
                setDepositos(result.data);
                setTotalPages(result.totalPages);
            }
        } catch (err) {
            setError('Error al cargar depósitos.');
        } finally {
            setLoading(false);
        }
    }, [selectedSucursal, isAccount, isAggregator, currentPage]);

    // 3. Función para obtener resumen (usando el servicio)
    const loadMonthlySummary = useCallback(async () => {
        const accountType = computeAccountType();
        if (!accountType) return;

        setSummaryLoading(true);
        let accessToken, bpToken = null;

        if (isAccount && !isAggregator) {
            accessToken = selectedSucursal ? selectedSucursal.access_token : localStorage.getItem('access_token');
            bpToken = selectedSucursal ? selectedSucursal.bp_token : localStorage.getItem('bp_token');
        } else {
            accessToken = localStorage.getItem('access_token');
        }

        try {
            const sum = await fetchMonthlySum({ accessToken, bpToken, accountType });
            setMonthlyTotal(sum);
        } catch (err) {
            console.error('Error al cargar el resumen mensual');
        } finally {
            setSummaryLoading(false);
        }
    }, [selectedSucursal, isAccount, isAggregator]);

    // 4. Efectos para disparar la carga
    useEffect(() => {
        loadDepositos();
    }, [loadDepositos]);

    useEffect(() => {
        loadMonthlySummary();
    }, [loadMonthlySummary]);

    const handleSucursalSelect = (sucursal) => {
        setSelectedSucursal(sucursal);
        setDepositos([]);
        setCurrentPage(1);
    };

    return (
        <div className="Movimientos">
            <NavBarPeiix />

            {/* Header y Selector de Sucursal */}
            <div className='navbar-container' style={{ padding: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className='navbar-imgl' style={{ flex: '0 0 auto' }}>
                    <h3 className=" fw-bold" style={{ color: '#3E3E3E', fontFamily: 'Montserrat, sans-serif', marginBottom: '0', display: 'flex', alignItems: 'center' }}>
                        <CDBIcon icon="dollar-sign" style={{ marginRight: '5px' }} />
                        Depósitos
                    </h3>
                </div>
                {(isAccount && sucursales.length > 1) && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexGrow: 1 }}>
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                            <DropdownToggle caret style={{ height: 'auto', borderRadius: '5px', border: '1px solid #F2F2F2', backgroundColor: '#F2F2F2', color: '#3E3E3E', fontWeight: 'bold', fontFamily: 'Montserrat, sans-serif', whiteSpace: 'nowrap' }}>
                                <CDBIcon icon="map-marker-alt" />
                                {selectedSucursal ? selectedSucursal.nombre : "Sucursales"}
                            </DropdownToggle>
                            <DropdownMenu>
                                {sucursales.map((sucursal, index) => (
                                    <DropdownItem key={index} onClick={() => handleSucursalSelect(sucursal)}>
                                        {sucursal.nombre}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                )}
            </div>

            {/* Resumen Mensual */}
            <MovementsSummary monthlyTotal={monthlyTotal} loading={summaryLoading} />

            <div style={{ padding: '20px 50px', fontSize: '14px', color: '#666', fontFamily: 'Montserrat, sans-serif' }}>
                Nota: Este importe puede variar si tiene contracargos en proceso.
            </div>

            {/* Tabla de Movimientos */}
            <div style={{ padding: '0 50px' }}>
                <MovementsTable 
                    depositos={depositos} 
                    isAccount={isAccount} 
                    accessToken={isAccount && !isAggregator 
                        ? (selectedSucursal ? selectedSucursal.access_token : localStorage.getItem('access_token'))
                        : localStorage.getItem('access_token')
                    }
                />
            </div>

            {/* Paginación */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                    {[...Array(totalPages).keys()].slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2)).map(number => (
                        <Pagination.Item key={number + 1} active={number + 1 === currentPage} onClick={() => setCurrentPage(number + 1)}>
                            {number + 1}
                        </Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
            </div>

            {/* Loading Overlay */}
            <LoadingOverlay show={loading} message="Cargando sus depósitos..." />
            
            {error && (
                <div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>{error}</div>
            )}
        </div>
    );
};

export default Movimientos;
